<template>
  <skeleton :content-ready="!!article">
    <div>
      <floating-nav :nav-list="sideNavLinks"></floating-nav>
      <article class="bg-bg-2 text-center">
        <main class="article-main">
          <section
            v-for="(section, index) of article.sections"
            :id="`article-section-${index}`"
            :key="`article-section-${index}`"
            class="max-section-height section margin-max-bottom flex-reverse">
            <div
              class="section-inner max-size-parent animate__fadeIn"
              :class="{ 'flex-center': !section.image, 'even-flex': !!section.image }">
              <div class="">
                <div class="section-text-wrapper">
                  <h2 class="margin-l-bottom small-screen-margin-top">
                    {{ section.title }}
                  </h2>
                  <content-blocks :content="section.content"></content-blocks>
                </div>
              </div>
              <div
                v-if="!!section.image"
                class="img-container small-screen-margin-top animate__fadeIn">
                <img
                  :src="section.image"
                  :alt="section.imageAlt"
                  class="clip-picture border-gray-2-xs radius-m" />
                <figure class="margin-2xl-vertical">
                  <figcaption class="content-image-caption">
                    {{ section.imageCaption }}
                  </figcaption>
                </figure>
              </div>
            </div>
          </section>
        </main>
      </article>
    </div>
  </skeleton>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations.js";
import { reactive, computed } from "vue";
import BaseSideNav from "@/client/components/nav/BaseSideNav.vue";
import FloatingNav from "@/client/applications/justopp/components/nav/FloatingNav";

export default {
  components: { BaseSideNav, FloatingNav },
  layout: {
    component: "noContainer",
    params: reactive({
      title: "core.article.genericTitle",
      // subTitle: "justopp.aboutSection.pageSubtitle",
    }),
  },
  middleware: {},
  props: {
    alias: {
      type: String,
      default: "",
    },

    offset: -120,
  },
  setup() {
    const { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations();
    return { asyncOps, asyncOpsReady, asyncStatus };
  },
  meta: {
    title: "core.article.genericBrowserTitle",
  },
  data() {
    return {
      article: false,
      asyncData: {
        article: {
          target: "content/article/" + this.alias,
          data: {},
          options: {
            dataMutator: (data) => (data.item ? data.item : false),
            errorHandler: "404",
          },
        },
      },
    };
  },
  computed: {
    sideNavLinks() {
      if (!this.article.sections.length) {
        return [];
      } else {
        return this.article.sections.map((section, index) => {
          return {
            label: section.title,
            scrollTargetSelector: `article-section-${index}`,
            class: "",
            to: "",
            index: index,
          };
        });
      }
    },
  },
  watch: {
    async article(newVal) {
      if (!newVal) {
        return;
      }

      let layoutConf = this[config.layoutAccessorKey]();
      if (newVal.title) {
        layoutConf.params.title = newVal.title;
      }

      if (newVal.subTitle) {
        layoutConf.params.subTitle = newVal.subTitle;
      }

      if (newVal.metaTitle) {
        this.meta.title = newVal.metaTitle;
      }
    },
    $route: {
      handler(newVal) {
        this.handleHash();
      },
      immediate: true,
    },
  },
  methods: {
    async scrollToElement(el) {
      if (utilities.isSSR()) {
        return true;
      }

      const y = el.getBoundingClientRect().top + window.pageYOffset + this.offset;
      window.scrollTo({ top: y, behavior: "smooth" });
    },
    async scrollToSectionByHash(hash) {
      if (utilities.isSSR()) {
        return true;
      }

      if (!hash) {
        return true;
      }
      await utilities.wait(300);

      await utilities.waitUntil(async () => {
        return !!this.article;
      });

      try {
        let anchor = document.querySelector(hash);
        this.scrollToElement(anchor);
      } catch (e) {
        setTimeout(() => {
          let anchor = document.querySelector(hash);
          this.scrollToElement(anchor);
        }, 300);
      }
    },
    async handleHash() {
      if (!this.$route.hash) {
        return true;
      }
      await utilities.waitUntil(async () => !!this.article, 100);
      this.scrollToSectionByHash(this.$route.hash);
    },
  },
};
</script>

<style lang="scss" scoped>
.clip-picture {
  height: auto;
  aspect-ratio: 1.13;
  max-width: 20vw;
  max-height: 52vh;

  @media (max-width: 1024px) {
    height: auto;
    aspect-ratio: 1.13;
    max-width: 70%;
    max-height: 60vh;
    justify-content: center;
    flex-direction: column;
  }
}

.section-text-wrapper {
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--margin-l);
}

.content-image {
  max-width: 100%;
  object-fit: cover;
  object-position: 100% 0;
}

.content-image-caption {
  font-style: italic;
}

.img-container {
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  padding: var(--margin-xl);
  @media (max-width: 1024px) {
    display: block;
    padding: 0;
  }
}

.bottom-border-gray-thin {
  border-bottom: 2px solid var(--c-gray);
}

.even-flex {
  display: flex;
  justify-content: space-evenly;
}

.flex-reverse:nth-of-type(even) {
  background-color: var(--c-lead);
  clip-path: polygon(0% 8%, 100% 0%, 100% 92%, 0% 100%, 0% 100%);

  @media (max-width: 1024px) {
    clip-path: polygon(0% 2%, 100% 0%, 100% 98%, 0% 100%, 0% 100%);
  }

  &:last-child {
    margin-bottom: calc(var(--margin-max) * 1.5) !important;
  }

  .section-inner {
    flex-direction: row-reverse;

    @media (max-width: 1024px) {
      padding: var(--margin-2xl) !important;
      flex-direction: column;
    }
  }
}

.section-inner {
  width: 100%;
  padding: calc(var(--margin-2xl) * 3) !important;

  @media (max-width: 1024px) {
    padding: var(--margin-2xl) !important;
    flex-direction: column;
  }

  :deep(ul), :deep(p)  {
    text-align: start;
  }

  :deep(ul  li ) {
    text-align: start;
    margin-bottom: var(--margin-m)
  }
}

.max-section-height {
  //height: var(--width-800);

  @media (max-width: 1024px) {
    //height: var(--width-900);
  }
}

.small-screen-margin-top {
  @media (max-width: 1024px) {
    margin-top: var(--margin-2xl);
  }
}

.formatted-ratio {
  width: 600px;
  position: relative;
  padding-bottom: 0;
  height: 300px;
  padding-top: 60px;
}

:deep(.dashboard-nav ul) {
  justify-content: center;
}
</style>
